import { fetchRedeemProviderBalance } from "@shared/api";
import Jackpots from "@shared/components/Jackpots/Jackpots";
import {
  useCurrentProvider,
  useIsJackpotsShown,
} from "@shared/hooks/games/game-providers";
import {
  FooterDesktop,
  useIsSweepstakesEnabled,
} from "@shared/modules/sweepstakes";
import cx from "classnames";
import React, { createRef, memo, useEffect } from "react";
import { useHistory } from "react-router-dom";

import { GameFrame } from "../../Game/GameFrame";

const theme = process.env.REACT_APP_THEME;
const isLuckyClubTheme = theme === "luckyclub";
const OFFSET_TOP = isLuckyClubTheme ? 120 : 36;
const MARGIN_BOTTOM = 38;
const HORIZONTAL_MARGIN = window.devicePixelRatio * 32;
const FULLSCREEN_GAME_PROVIDERS = ["poker", "BettingSoftware", "virtual"];

const GameContent = ({
  session,
  fullscreen,
  toggleFullscreen,
  aspectRatio = 4 / 3,
  frameSettings,
  wallet_integration,
}) => {
  const containerRef = createRef();
  const modalRef = createRef();

  const isJackpotsShown = useIsJackpotsShown();
  const currentProvider = useCurrentProvider();
  const isSweepstakesEnabled = useIsSweepstakesEnabled() ?? {};
  const history = useHistory();

  useEffect(() => {
    if (
      !fullscreen &&
      FULLSCREEN_GAME_PROVIDERS.includes(currentProvider?.string_id)
    ) {
      toggleFullscreen();
    }
  }, [currentProvider?.string_id]);

  useEffect(() => {
    const setModalSize = () => {
      const container = containerRef.current;
      const modal = modalRef.current;

      if (!container || !modal) {
        return;
      }

      let { width: containerWidth, height: containerHeight } =
        container.getBoundingClientRect();

      containerWidth -= HORIZONTAL_MARGIN;
      containerHeight -= OFFSET_TOP + MARGIN_BOTTOM;

      const containerAspectRatio = containerWidth / containerHeight;

      if (containerAspectRatio >= aspectRatio) {
        const modalHeight = containerHeight;
        const modalWidth = modalHeight * aspectRatio;

        modal.style.width = modalWidth + "px";
        modal.style.height = modalHeight + "px";
      } else {
        const modalWidth = containerWidth;
        const modalHeight = modalWidth / aspectRatio;

        modal.style.width = modalWidth + "px";
        modal.style.height = modalHeight + "px";
      }
    };
    setModalSize();

    const handleWindowResize = () => setModalSize();
    window.addEventListener("resize", handleWindowResize);

    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, [containerRef, modalRef, aspectRatio]);

  async function handleGameClose() {
    try {
      if (wallet_integration && session) {
        await fetchRedeemProviderBalance();
      }
    } catch (e) {
      throw new Error(e);
    } finally {
      history.push("/");
    }
  }

  return (
    <>
      {isSweepstakesEnabled && <FooterDesktop />}
      <div
        className={cx("scrollarea", {
          "scrollarea--sweepstakes": isSweepstakesEnabled,
        })}
      >
        <div
          ref={containerRef}
          className="scrollarea__inner"
          style={{
            position: "relative",
            justifyContent: "center",
            overflow: "hidden",
            marginBottom: isSweepstakesEnabled ? 40 : 0,
          }}
        >
          <div
            style={{ padding: 0 }}
            className={cx("scrollarea__content", {
              scrollarea__content_fullscreen: fullscreen,
            })}
          >
            {isJackpotsShown && <Jackpots isCompact />}

            <div
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <div
                ref={modalRef}
                className={cx("modal modal_game", {
                  "modal_game--message": !session,
                  "modal_game--full": fullscreen,
                  "modal_game--sweepstakes": isSweepstakesEnabled,
                })}
              >
                <GameFrame session={session} settings={frameSettings} />

                <div className="modal__tools">
                  <div className="ico ico-close" onClick={handleGameClose} />
                  <i
                    className="ico ico-resize-out"
                    onClick={toggleFullscreen}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default memo(GameContent);
